import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GripVertical, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { cmsPartnerApi, cmsTeamApi, cmsTeacherApi } from '../../lib/api/cms';

interface Entity {
  id: number;
  name: string;
  standort_ids?: number[];
  fach_ids?: number[];
}

/** Per-standort value: hidden ids + display order (both scoped to this standort). */
export type EntityToggleValue = number[] | { hidden?: number[]; order?: number[] };

const SOURCE_LABEL: Record<string, string> = {
  partner: 'FamilyCard',
  team: 'Team',
  teacher: 'Personen (Lehrerprofile)',
};

const asIntList = (v: unknown): number[] =>
  Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];

/** Normalise the (possibly legacy-array) field value into { hidden, order }. */
function normalise(value: EntityToggleValue): { hidden: number[]; order: number[] } {
  if (Array.isArray(value)) return { hidden: asIntList(value), order: [] };
  return { hidden: asIntList(value?.hidden), order: asIntList(value?.order) };
}

/**
 * Per-standort visibility toggle + drag-and-drop ordering for centrally-managed
 * entities (FamilyCard partners or team members) assigned to this standort.
 *
 * Both the hidden ids and the display order are stored per standort in the
 * standort content (this field's value), so the same entity can appear in a
 * different order on different standort pages. Saved/published with the rest of
 * the standort content.
 */
export const StandortEntityToggleField: React.FC<{
  source: string;
  standortId: number;
  /** When set (standort×fach editor), only entities assigned to this Fach are listed. */
  fachId?: number;
  value: EntityToggleValue;
  onChange: (v: { hidden: number[]; order: number[] }) => void;
}> = ({ source, standortId, fachId, value, onChange }) => {
  const [items, setItems] = useState<Entity[] | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { hidden, order } = normalise(value);

  const load = useCallback(async () => {
    try {
      const rows =
        source === 'team'
          ? await cmsTeamApi.list()
          : source === 'teacher'
            ? await cmsTeacherApi.list()
            : await cmsPartnerApi.list();
      setItems(rows as Entity[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Konnte nicht geladen werden');
    }
  }, [source]);

  useEffect(() => { void load(); }, [load]);

  // Assigned subset (mirrors the website: empty standort_ids = all), ordered by
  // this standort's saved order; unordered/new entries keep their default order.
  const ordered = useMemo(() => {
    if (!items) return [];
    const assigned = items.filter((e) => {
      const ids = e.standort_ids || [];
      if (ids.length > 0 && !ids.includes(standortId)) return false;
      // Fach-scoped variant (standort×fach): only entities with this Fach assigned.
      if (fachId !== undefined) return (e.fach_ids || []).includes(fachId);
      return true;
    });
    const rank = (id: number) => {
      const i = order.indexOf(id);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...assigned].sort((a, b) => rank(a.id) - rank(b.id));
  }, [items, standortId, fachId, order]);

  const toggle = (id: number, visible: boolean) => {
    const nextHidden = visible ? hidden.filter((x) => x !== id) : hidden.includes(id) ? hidden : [...hidden, id];
    onChange({ hidden: nextHidden, order });
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setDragIndex(null);
    onChange({ hidden, order: next.map((e) => e.id) });
  };

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!items) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Lädt …
      </div>
    );
  }
  if (ordered.length === 0) {
    const where =
      source === 'teacher'
        ? `in den Personen (Lehrerprofil: Standorte + ${getSubjectLabels().plural})`
        : `unter CMS > ${SOURCE_LABEL[source] ?? source}`;
    return (
      <p className="text-sm text-muted-foreground">
        {fachId !== undefined
          ? `Diesem Standort ist kein Lehrer mit diesem ${getSubjectLabels().singular} zugeordnet. Standort und ${getSubjectLabels().singular} zuordnen ${where}.`
          : `Diesem Standort sind keine Einträge zugeordnet. Zuordnen ${where}.`}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Reihenfolge per Ziehen am Griff ändern – sie gilt nur für diesen Standort. Nach dem
        Speichern/Veröffentlichen erscheint sie so auf der Standortseite.
      </p>
      <div className="divide-y rounded-md border">
        {ordered.map((entity, index) => {
          const visible = !hidden.includes(entity.id);
          return (
            <div
              key={entity.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(index)}
              className={`flex items-center gap-2 px-3 py-2 ${dragIndex === index ? 'opacity-60' : ''}`}
            >
              <button
                type="button"
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => setDragIndex(null)}
                className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
                aria-label="Zum Sortieren ziehen"
                title="Zum Sortieren ziehen"
              >
                <GripVertical className="h-4 w-4" />
              </button>
              <span className="min-w-0 flex-1 truncate text-sm">{entity.name}</span>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Switch
                  checked={visible}
                  onCheckedChange={(v: boolean) => toggle(entity.id, v)}
                  aria-label="Auf der Standortseite anzeigen"
                />
                Anzeigen
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StandortEntityToggleField;
